import {useState, useEffect} from 'react';
import {observer} from 'mobx-react';
import {auth, endpoints} from 'main/tools';
import {Panel, Button, Select2, Input, FormError} from 'shared/components';
import {FormattedMessage} from 'react-intl';
import {formattedTimeFromSeconds, timestampDate} from 'shared/tools';
import {withState} from 'shared/core';
import useModal from 'shared/hooks/useModal';
import TimeClockState from '../state/TimeClockState';
import LocationErrorModal from './LocationErrorModal';
import moment from 'moment';

const Timer = observer(({clockInTime}) => {
  const [clockedInSeconds, setClockedInSeconds] = useState(moment().diff(moment(clockInTime), 'seconds'));

  useEffect(() => {
    if (!clockInTime) {
      setClockedInSeconds(0);
      return;
    }

    const interval = setInterval(() => {
      setClockedInSeconds(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [clockInTime]);

  return (
    <div className='bold flex align-items-middle' style={{fontSize: 36}}>
      <div
        className='mr1'
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: clockInTime ? '#54C379' : '#919EAB',
          boxShadow: clockInTime ? '0 0 8px #54C37988' : 'none',
        }}
      />
      <div>{formattedTimeFromSeconds(clockedInSeconds)}</div>
    </div>
  );
});

const Projects = observer(({uiState}) => {
  const {clockInEvent, projectId, employee} = uiState;

  return (
    <div className='col-12 md-col-6 md-pr1'>
      <Select2
        searchable
        allowClear
        disabled={!!clockInEvent}
        label='time_tracking.time_clock.Project'
        onChange={e => uiState.updateProjectId(e.target.value)}
        defaultValue={clockInEvent?.projectId}
        value={projectId}
        by='name'
        endpoint={endpoints.TIME_TRACKING.PROJECTS.FOR_EMPLOYEE.with(employee.id)}
      >
        <option value={clockInEvent?.projectId}>{clockInEvent?.projectName}</option>
      </Select2>
    </div>
  );
});

const Note = observer(({uiState}) => {
  const {clockInEvent, note} = uiState;

  return (
    <div className='col-12 md-col-6 md-pl1'>
      <Input
        disabled={!!clockInEvent}
        label='time_tracking.time_clock.Note'
        value={note}
        onChange={e => uiState.updateNote(e.target.value)}
      />
    </div>
  );
});

const TimeClock = observer(({uiState}) => {
  const {clockInEvent, errors, isHidden, employee, showAction} = uiState;
  const locationErrorModalOpen = useModal(uiState, 'LocationErrorModal');

  if (isHidden) return null;

  return (
    <Panel>
      <div className='mb2'>
        <div className='flex flex-col md-flex-row justify-between row-gap-2'>
          <div className='flex flex-col align-items-start'>
            <div className='jumbo'>
              {clockInEvent ?
                <FormattedMessage id='time_tracking.time_clock.CLOCKED_IN_AT' values={{time: timestampDate(clockInEvent.createdAt, {hideToday: true}).toLowerCase()}}/> :
                <FormattedMessage id='time_tracking.time_clock.Not clocked in'/>
              }
            </div>
            <Timer clockInTime={clockInEvent?.createdAt}/>
          </div>
          {showAction && <div>
            <Button className='full-width' onClick={() => clockInEvent ? uiState.clockOut() : uiState.clockIn()} trait={clockInEvent ? 'destructive' : 'primary'}>
              <div className='flex align-items-middle'>
                <i className='material-icons mr1 h4'>
                  {clockInEvent ? 'stop' : 'play_arrow'}
                </i>
                {clockInEvent ?
                  employee.id !== auth.employee.id ?
                    <FormattedMessage id='time_tracking.time_clock.CLOCK_OUT_WITH_NAME' values={{name: employee.firstName}}/> :
                    <FormattedMessage id='time_tracking.time_clock.Clock Out'/> :
                  <FormattedMessage id='time_tracking.time_clock.Clock In'/>
                }
              </div>
            </Button>
          </div>}
        </div>
        <FormError message={errors.base}/>
      </div>
      <Panel.Separator/>
      <div className='flex flex-col md-flex-row row-gap-2'>
        <Projects uiState={uiState}/>
        <Note uiState={uiState}/>
      </div>
      <LocationErrorModal modalOpen={locationErrorModalOpen} uiState={uiState}/>
    </Panel>
  );
});

export default withState(TimeClock, TimeClockState);
